import { useEffect, useState, useMemo } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Euro, Plus, Trash2, Save, Loader2, Pencil, X, Clock,
  Info, TrendingUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import NumInputFR from '@/components/NumInputFR';

// Presets de types de tarif proposés dans un menu déroulant à l'ajout
const TARIF_PRESETS = [
  { label: 'Tarif journalier',      unite: '€/j' },
  { label: 'Tarif horaire',         unite: '€/h' },
  { label: 'Prix au m²',            unite: '€/m²' },
  { label: 'Prix au mètre linéaire', unite: '€/ml' },
  { label: 'Forfait déplacement',   unite: '€' },
  { label: 'Forfait intervention',  unite: '€' },
  { label: 'Forfait mise en service', unite: '€' },
  { label: 'Marge matériaux',       unite: '%' },
  { label: 'Autre',                 unite: '' },
];

const fmtMontant = (v, unite) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (Number.isNaN(n)) return String(v);
  const s = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n);
  return `${s}${unite ? ' ' + unite : ''}`;
};

const newTarifRow = (preset) => ({
  id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  label: preset?.label || '',
  montant: null,
  unite: preset?.unite || '',
  notes: '',
});

/**
 * Bloc « Tarifs » modifiable inline — plusieurs lignes libres.
 * Chaque ligne : label, montant, unité, notes (toutes optionnelles).
 * Sauvegarde via PUT /api/sous-traitants/{id}.
 */
export default function TarifsPanel({ sousTraitant, onSaved }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPresets, setShowPresets] = useState(false);

  // Sync rows from prop
  useEffect(() => {
    setRows(Array.isArray(sousTraitant?.tarifs_detail) ? sousTraitant.tarifs_detail : []);
  }, [sousTraitant?.sous_traitant_id, sousTraitant?.tarifs_detail]);

  const updateRow = (id, patch) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const removeRow = (id) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const addRow = (preset) => {
    setRows((prev) => [...prev, newTarifRow(preset)]);
    setShowPresets(false);
    setEditing(true);
  };

  const cancelEdit = () => {
    setRows(Array.isArray(sousTraitant?.tarifs_detail) ? sousTraitant.tarifs_detail : []);
    setEditing(false);
    setShowPresets(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Nettoyage : retire les lignes complètement vides
      const cleaned = rows
        .filter((r) => (r.label && r.label.trim()) || r.montant !== null && r.montant !== undefined && r.montant !== '')
        .map((r) => ({
          id: r.id,
          label: (r.label || '').trim(),
          montant: r.montant === '' || r.montant === undefined ? null : r.montant,
          unite: (r.unite || '').trim(),
          notes: (r.notes || '').trim(),
        }));
      // On envoie l'objet complet (le back accepte un PUT complet)
      const payload = { ...sousTraitant, tarifs_detail: cleaned };
      delete payload.sous_traitant_id;
      delete payload.date_creation;
      delete payload._id;
      await api.put(`/sous-traitants/${sousTraitant.sous_traitant_id}`, payload);
      toast.success('Tarifs enregistrés');
      setEditing(false);
      setShowPresets(false);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const isEmpty = !rows || rows.length === 0;

  // Regroupe par métier (pour affichage lecture)
  const rowsGrouped = useMemo(() => {
    const g = {};
    const order = [];
    (rows || []).forEach((r) => {
      const key = r.trade || 'Autres';
      if (!g[key]) { g[key] = []; order.push(key); }
      g[key].push(r);
    });
    // Tarifs généraux en dernier
    const general = order.filter((t) => /tarifs\s*g[ée]n[ée]raux/i.test(t) || t === 'Universel');
    const others = order.filter((t) => !general.includes(t));
    return { g, order: [...others, ...general] };
  }, [rows]);

  const dateMaj = sousTraitant?.tarifs_date_maj;
  const dateMajFmt = dateMaj ? new Date(dateMaj).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : null;
  const tarifTypeLabels = {
    'main_oeuvre': "Main-d'œuvre uniquement",
    'main_oeuvre_consommables': "Main-d'œuvre + consommables",
    'fourniture_main_oeuvre': "Fourniture + main-d'œuvre",
  };
  const tarifTypeLabel = tarifTypeLabels[sousTraitant?.tarif_type];

  const showSummary = !editing && (sousTraitant?.tarif_journalier || sousTraitant?.tarif_horaire
    || sousTraitant?.min_facturation || sousTraitant?.forfait_deplacement || tarifTypeLabel || dateMajFmt);

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200" data-testid="tarifs-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Euro className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Tarifs
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <Button
                variant="ghost" size="sm"
                onClick={cancelEdit}
                disabled={saving}
                data-testid="tarifs-cancel-btn"
              >
                <X className="w-4 h-4 mr-1" /> Annuler
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="tarifs-save-btn"
              >
                {saving
                  ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Enregistrement…</>
                  : <><Save className="w-4 h-4 mr-1" />Enregistrer</>}
              </Button>
            </>
          ) : (
            <Button
              variant="outline" size="sm"
              onClick={() => setEditing(true)}
              data-testid="tarifs-edit-btn"
            >
              <Pencil className="w-3.5 h-3.5 mr-1.5" /> Modifier
            </Button>
          )}
        </div>
      </div>

      {/* Summary card — tarifs clés + type + date de mise à jour */}
      {showSummary && (
        <div className="mb-4 bg-gradient-to-br from-blue-50 to-slate-50 rounded-lg border border-blue-100 p-4" data-testid="tarifs-summary">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
            <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider font-semibold">
              <TrendingUp className="w-3.5 h-3.5" />
              Tarifs clés
            </div>
            {dateMajFmt && (
              <div className="text-xs text-slate-500 flex items-center gap-1.5">
                <Clock className="w-3 h-3" />
                Dernière mise à jour : <b className="text-slate-700">{dateMajFmt}</b>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <ShortcutBox label="Journalier" value={sousTraitant?.tarif_journalier} unit="€/j" />
            <ShortcutBox label="Horaire" value={sousTraitant?.tarif_horaire} unit="€/h" />
            <ShortcutBox label="Déplacement" value={sousTraitant?.forfait_deplacement} unit="€" />
            <ShortcutBox label="Minimum facturé" value={sousTraitant?.min_facturation} unit="€" />
          </div>
          {tarifTypeLabel && (
            <div className="mt-3 flex items-center gap-2 text-xs text-slate-600">
              <Info className="w-3.5 h-3.5 text-blue-600" />
              <span>Ses tarifs comprennent : <b className="text-slate-800">{tarifTypeLabel}</b></span>
            </div>
          )}
        </div>
      )}

      {/* Vue lecture groupée par métier */}
      {!editing && !isEmpty && (
        <div className="space-y-4" data-testid="tarifs-grouped">
          {rowsGrouped.order.map((trade) => (
            <div key={trade}>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                <span className={/tarifs\s*g[ée]n[ée]raux/i.test(trade) || trade === 'Universel' ? 'text-blue-700' : ''}>
                  {/tarifs\s*g[ée]n[ée]raux/i.test(trade) || trade === 'Universel' ? 'Tarifs généraux' : trade}
                </span>
                <span className="text-slate-300">·</span>
                <span className="text-slate-400 normal-case tracking-normal">{rowsGrouped.g[trade].length} prestation{rowsGrouped.g[trade].length > 1 ? 's' : ''}</span>
              </div>
              <div className="overflow-hidden border border-slate-100 rounded-lg">
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-slate-100">
                    {rowsGrouped.g[trade].map((r) => (
                      <tr key={r.id} data-testid={`tarif-row-${r.id}`}>
                        <td className="px-3 py-2 text-slate-900">{r.label || '—'}</td>
                        <td className="px-3 py-2 text-right font-semibold text-blue-700 tabular-nums whitespace-nowrap w-32">
                          {fmtMontant(r.montant, '')}
                        </td>
                        <td className="px-3 py-2 text-slate-500 text-xs w-20">{r.unite || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {isEmpty && !editing ? (
        <div className="text-center py-6 text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg">
          Aucun tarif renseigné. Cliquez sur « Modifier » pour en ajouter.
        </div>
      ) : editing ? (
        <div className="overflow-hidden border border-slate-100 rounded-lg">
          <table className="w-full text-sm" data-testid="tarifs-table">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Prestation</th>
                <th className="px-3 py-2 text-right font-semibold w-32">Montant</th>
                <th className="px-3 py-2 text-left font-semibold w-24">Unité</th>
                <th className="px-3 py-2 text-left font-semibold hidden md:table-cell">Notes</th>
                {editing && <th className="px-2 py-2 w-10" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`tarif-row-${r.id}`}>
                  {editing ? (
                    <>
                      <td className="px-3 py-2">
                        <Input
                          value={r.label || ''}
                          onChange={(e) => updateRow(r.id, { label: e.target.value })}
                          placeholder="Ex : Prix au m²"
                          className="h-8"
                          data-testid={`tarif-label-${r.id}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <NumInputFR
                          value={r.montant ?? ''}
                          onChange={(v) => updateRow(r.id, { montant: v })}
                          placeholder="0"
                          className="h-8 text-right"
                          data-testid={`tarif-montant-${r.id}`}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          value={r.unite || ''}
                          onChange={(e) => updateRow(r.id, { unite: e.target.value })}
                          placeholder="€/j, €/m²…"
                          className="h-8"
                          data-testid={`tarif-unite-${r.id}`}
                        />
                      </td>
                      <td className="px-3 py-2 hidden md:table-cell">
                        <Textarea
                          value={r.notes || ''}
                          onChange={(e) => updateRow(r.id, { notes: e.target.value })}
                          placeholder="Précisions (optionnel)"
                          rows={1}
                          className="min-h-8 py-1 text-sm"
                          data-testid={`tarif-notes-${r.id}`}
                        />
                      </td>
                      <td className="px-2 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => removeRow(r.id)}
                          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                          data-testid={`tarif-delete-${r.id}`}
                          title="Supprimer cette ligne"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-3 py-2 text-slate-900 font-medium">{r.label || '—'}</td>
                      <td className="px-3 py-2 text-right font-semibold text-blue-700 tabular-nums">
                        {fmtMontant(r.montant, '')}
                      </td>
                      <td className="px-3 py-2 text-slate-600">{r.unite || '—'}</td>
                      <td className="px-3 py-2 text-slate-500 text-xs hidden md:table-cell">{r.notes || ''}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {editing && (
        <div className="mt-3 relative">
          <Button
            variant="outline" size="sm"
            onClick={() => setShowPresets((v) => !v)}
            className="border-blue-200 text-blue-700 hover:bg-blue-50"
            data-testid="tarifs-add-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Ajouter une ligne
          </Button>
          {showPresets && (
            <div
              className="absolute mt-1 z-20 bg-white border border-slate-200 rounded-lg shadow-lg min-w-[240px] py-1"
              data-testid="tarifs-presets-menu"
            >
              <p className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                Choisir un type
              </p>
              {TARIF_PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => addRow(p)}
                  className="w-full px-3 py-1.5 text-sm text-left hover:bg-blue-50 flex items-center justify-between gap-3"
                  data-testid={`tarifs-preset-${p.label}`}
                >
                  <span className="text-slate-800">{p.label}</span>
                  {p.unite && <span className="text-xs text-slate-400">{p.unite}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ShortcutBox({ label, value, unit }) {
  const has = value !== null && value !== undefined && value !== '';
  return (
    <div className={`rounded-md border ${has ? 'border-blue-200 bg-white' : 'border-slate-200 bg-slate-50'} p-2`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold truncate">{label}</div>
      {has ? (
        <div className="text-base font-bold text-slate-900 tabular-nums mt-0.5">
          {new Intl.NumberFormat('fr-FR').format(value)} <span className="text-xs font-normal text-slate-500">{unit}</span>
        </div>
      ) : (
        <div className="text-sm text-slate-300 mt-0.5">—</div>
      )}
    </div>
  );
}
